import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const quoteSchema = z.object({
  serviceId: z.string().uuid(),
  packageId: z.string().uuid().optional(),
  quantity: z.number().int().min(1).max(10).optional(),
  extras: z.array(z.object({ id: z.string().uuid(), quantity: z.number().int().min(0).max(20) })).optional(),
});

export const getOrderQuote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => quoteSchema.parse(input))
  .handler(async ({ data }) => {
    const { quoteOrder } = await import("./orders.server");
    return quoteOrder(data);
  });

export const placeMarketplaceOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => quoteSchema.extend({ idempotencyKey: z.string().max(80).optional() }).parse(input))
  .handler(async ({ data, context }) => {
    const { placeOrder } = await import("./orders.server");
    return placeOrder(context.userId, data);
  });

export const listMyMarketplaceOrders = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ role: z.enum(["buyer", "seller"]) }).parse(input))
  .handler(async ({ data, context }) => {
    const { listOrders } = await import("./orders.server");
    return listOrders(context.userId, data.role);
  });

export const getMarketplaceOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ orderId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { getOrderDetail } = await import("./orders.server");
    return getOrderDetail(context.userId, data.orderId);
  });

export const submitOrderRequirements = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        orderId: z.string().uuid(),
        answers: z.array(z.object({ id: z.string().uuid(), answer: z.string().max(4000) })),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { submitRequirements } = await import("./orders.server");
    await submitRequirements(context.userId, data.orderId, data.answers);
    return { ok: true };
  });

export const deliverMarketplaceOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ orderId: z.string().uuid(), message: z.string().min(3).max(4000) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { deliverOrder } = await import("./orders.server");
    await deliverOrder(context.userId, data.orderId, data.message);
    return { ok: true };
  });

export const requestOrderRevision = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ orderId: z.string().uuid(), reason: z.string().min(3).max(2000) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { requestRevision } = await import("./orders.server");
    await requestRevision(context.userId, data.orderId, data.reason);
    return { ok: true };
  });

export const acceptMarketplaceDelivery = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ orderId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { acceptDelivery } = await import("./orders.server");
    return acceptDelivery(context.userId, data.orderId);
  });

export const cancelMarketplaceOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ orderId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { cancelOrder } = await import("./orders.server");
    return cancelOrder(context.userId, data.orderId);
  });

export const reviewMarketplaceOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ orderId: z.string().uuid(), rating: z.number().min(1).max(5), body: z.string().max(2000) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { leaveReview } = await import("./orders.server");
    await leaveReview(context.userId, data.orderId, data.rating, data.body);
    return { ok: true };
  });
